import React, { createContext, useContext, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { UserProfile } from '../types/user'

interface AuthContextType {
  user: UserProfile | null
  loading: boolean
  error: string | null
  isAuthenticated: boolean
  isEmailVerified: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  register: (userData: any) => Promise<void>
  sendPasswordReset: (email: string) => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>
  clearError: () => void
  refreshUserProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth()

  // Initialize Firestore data only when user is authenticated
  useEffect(() => {
    const initializeApp = async () => {
      if (auth.isAuthenticated && auth.user) {
        try {
          const { initializeFirestoreData } = await import('../services/firestore')
          await initializeFirestoreData()
        } catch (error) {
          console.warn('Firestore initialization failed (this is normal):', error)
          // This is expected behavior - initialization may fail if user doesn't have admin rights
        }
      }
    }

    initializeApp()
  }, [auth.isAuthenticated, auth.user])

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  )
} 